// Provably-fair worked-example generator — a faithful TypeScript port of the
// legacy web app's buildRulesExample()/computeDraw() (web/src/fairness/
// provably-fair-feature.js + web/src/games/game-globals.js). Deterministic:
// with the fixed example seed it reproduces the exact same numbers the legacy
// rules page shows when no live draw is available.

const ETH_BLOCK_BASE = 24_700_000; // ~ Apr 2026 anchor
const ROULETTE_RED_SET = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

// Fixed example (legacy fallback when no live draw exists).
const EXAMPLE_ROUND = 14823;
// 2026-05-07 12:25 BRT — must stay AFTER the 2026-04-29 algorithm fork the
// rules page footnote describes, or the worked example contradicts it.
const EXAMPLE_TS = 1_778_167_500;

export type HashChar = { c: string; isDigit: boolean; inLast10: boolean };

export type RulesBlock = {
  number: number;
  timeBR: string;
  timeUTC: string;
  ts: number;
  hashRaw: string;
  chars: HashChar[];
  last10: string[];
};

export type SicboStep = { idx: number; m: number; q: number; r: number; die: number };

export type RulesExample = {
  isLive: boolean;
  round: number;
  /** 这一期的开始时刻，秒。页面按它算当日期号 —— 全局期号只在链上核对时才有用。 */
  cutoffTs: number;
  dateLabel: string;
  blocks: RulesBlock[];
  sums: number[];
  D: number[];
  m1: number;
  m2: number;
  m3: number;
  /** 十位开奖数字（`D.join('')`）—— 轮盘除的就是这个数。 */
  dNumber: string;
  /**
   * 手算捷径的和：`D₁ + M₁ + M₂ + M₃`。
   *
   * <p>999 = 27 × 37，所以 1000 ≡ 1 (mod 37)：十进制数字三位一组相加不改变
   * 对 37 的余数——这是课本上判断 37 整除的方法。十位数除以 37 手算不了，
   * 这四段相加能，所以页面把它留作副行，给拿纸笔核对的人。
   */
  handSum: number;
  sicboSteps: SicboStep[];
  dice: number[];
  sicboSum: number;
  sicboBigSmall: 'big' | 'small';
  rouletteQ: number;
  rouletteR: number;
  rouletteNum: number;
  rouletteColor: 'red' | 'black' | 'green';
  lastdigit: number;
  ldOddEven: 'odd' | 'even';
  ldBigSmall: 'big' | 'small';
  ecCol: number;
  ecB1: number;
  ecB2: number;
  ecB3: number;
  ecSum: number;
  ecQ: number;
  ecR: number;
  ecD: number;
};

function blockNumberFor(round: number, offset: number): number {
  return ETH_BLOCK_BASE + round * 25 + offset;
}

// Deterministic seeded PRNG that produces the 64-hex-char mock block hash.
// Math.imul + >>>0 are load-bearing: they force 32-bit unsigned wraparound,
// so the output must match the legacy code exactly.
function mockHashFor(round: number, offset: number): string {
  let seed = (round * 1000 + offset * 137 + 0xdeadbeef) >>> 0;
  let out = '0x';
  for (let i = 0; i < 64; i++) {
    seed = (seed + 0x6d2b79f5) >>> 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed) >>> 0;
    t = ((t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t) >>> 0;
    out += ((t >>> 24) & 0xf).toString(16);
  }
  return out;
}

export function hashWalk(hashHex: string): { chars: HashChar[]; strip: string } {
  const body = hashHex.startsWith('0x') ? hashHex.slice(2) : hashHex;
  const digitPositions: number[] = [];
  for (let i = 0; i < body.length; i++) {
    if (body[i] >= '0' && body[i] <= '9') digitPositions.push(i);
  }
  const last10Set = new Set(digitPositions.slice(-10));
  const chars: HashChar[] = [];
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    const isDigit = c >= '0' && c <= '9';
    chars.push({ c, isDigit, inLast10: last10Set.has(i) });
  }
  const strip = digitPositions
    .slice(-10)
    .map((p) => body[p])
    .join('')
    .padStart(10, '0');
  return { chars, strip };
}

function rouletteColorOf(n: number): 'red' | 'black' | 'green' {
  if (n === 0) return 'green';
  return ROULETTE_RED_SET.has(n) ? 'red' : 'black';
}

type Draw = {
  round: number;
  cutoff_ts: number;
  blockNumbers: number[];
  blockTimestamps: number[];
  hashes: string[];
  walks: { chars: HashChar[]; strip: string }[];
  strips: string[];
};

function computeDraw(round: number, ts: number): Draw {
  const rawCutoff = ts || Math.floor(Date.now() / 1000);
  const cutoff = Math.floor(rawCutoff / 300) * 300;
  const blockNumbers = [blockNumberFor(round, 1), blockNumberFor(round, 2), blockNumberFor(round, 3)];
  const blockTimestamps = [cutoff + 14, cutoff + 26, cutoff + 38];
  const hashes = [mockHashFor(round, 1), mockHashFor(round, 2), mockHashFor(round, 3)];
  const walks = hashes.map((h) => hashWalk(h));
  const strips = walks.map((w) => w.strip);
  return { round, cutoff_ts: cutoff, blockNumbers, blockTimestamps, hashes, walks, strips };
}

function fmtBRTime(tsSec: number): string {
  if (!tsSec) return '';
  const d = new Date(tsSec > 1e12 ? tsSec : tsSec * 1000);
  return d.toLocaleString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function fmtUtcTime(tsSec: number): string {
  try {
    const d = new Date((tsSec || 0) * 1000);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    const ss = String(d.getUTCSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  } catch {
    return '';
  }
}

function fmtDateTimeBR(tsSec: number): string {
  if (!tsSec) return '';
  const ms = tsSec < 1e12 ? tsSec * 1000 : tsSec;
  try {
    const dateStr = new Date(ms).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const timeStr = new Date(ms).toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    // No "BRT": these helpers set the language to pt-BR but not the time zone,
    // so the clock is the viewer's own. Labelling it BRT was wrong for anyone
    // outside Brazil, and the product is heading to more countries.
    return `${dateStr} · ${timeStr}`;
  } catch {
    return '';
  }
}

export function buildRulesExample(): RulesExample {
  const draw = computeDraw(EXAMPLE_ROUND, EXAMPLE_TS);
  return deriveRulesExample({
    isLive: false,
    round: draw.round,
    cutoffTs: draw.cutoff_ts,
    blockNumbers: draw.blockNumbers,
    blockTimestamps: draw.blockTimestamps,
    hashes: draw.hashes,
  })!;
}

// Build the walkthrough from a REAL settled round (its on-chain anchors) so a
// reader can take the block numbers to Etherscan and match every hash. Returns
// null when the round lacks 3 hashed anchors — caller falls back to the
// synthetic example.
export function buildRulesExampleFromRound(round: {
  round_number?: number | string;
  cutoff_ts?: number;
  ends_at?: number;
  starts_at?: number;
  anchors?: Array<{ number?: number | string | null; hash?: string | null; timestamp?: number | null }>;
}): RulesExample | null {
  const anchors = (round.anchors || []).filter((anchor) => anchor && anchor.hash).slice(0, 3);
  if (anchors.length < 3) return null;
  const cutoff = Number(round.cutoff_ts || round.ends_at || round.starts_at || 0);
  if (!cutoff) return null;
  return deriveRulesExample({
    isLive: true,
    round: Number(round.round_number || 0),
    cutoffTs: cutoff,
    blockNumbers: anchors.map((anchor) => Number(anchor.number || 0)),
    blockTimestamps: anchors.map((anchor) => Number(anchor.timestamp || 0)),
    hashes: anchors.map((anchor) => String(anchor.hash)),
  });
}

function deriveRulesExample(source: {
  isLive: boolean;
  round: number;
  cutoffTs: number;
  blockNumbers: number[];
  blockTimestamps: number[];
  hashes: string[];
}): RulesExample | null {
  const walks = source.hashes.map((hash) => hashWalk(hash));
  const strips = walks.map((walk) => walk.strip);
  if (strips.length < 3 || strips.some((strip) => strip.length < 10)) return null;
  const { isLive } = source;
  const draw = {
    round: source.round,
    cutoff_ts: source.cutoffTs,
    blockNumbers: source.blockNumbers,
    blockTimestamps: source.blockTimestamps,
    hashes: source.hashes,
    walks,
    strips,
  };

  const sums: number[] = [];
  for (let i = 0; i < 10; i++) {
    sums.push(
      parseInt(draw.strips[0][i], 10) + parseInt(draw.strips[1][i], 10) + parseInt(draw.strips[2][i], 10),
    );
  }
  const D = sums.map((s) => s % 10);
  const m1 = parseInt(`${D[1]}${D[2]}${D[3]}`, 10);
  const m2 = parseInt(`${D[4]}${D[5]}${D[6]}`, 10);
  const m3 = parseInt(`${D[7]}${D[8]}${D[9]}`, 10);
  const sicboSteps: SicboStep[] = [m1, m2, m3].map((m, i) => {
    const q = Math.floor(m / 6);
    const r = m - q * 6;
    return { idx: i + 1, m, q, r, die: r + 1 };
  });
  const dice = sicboSteps.map((st) => st.die);
  const sicboSum = dice[0] + dice[1] + dice[2];
  // 轮盘除的是整个十位开奖数字，与后端 ops-node 的 draw.ts 同一条规则。
  // 旧式子 (M₁+M₂+M₃) % 37 其实是「九位数 D₂–D₁₀ ÷ 37」，D₁ 完全不参与。
  const dNumber = D.join('');
  const rQ = Number(BigInt(dNumber) / 37n);
  const rR = Number(BigInt(dNumber) % 37n);
  const handSum = D[0] + m1 + m2 + m3;
  const rouletteColor = rouletteColorOf(rR);
  const lastdigit = D[0];

  let exCol = 0;
  for (let i = 0; i < 10; i++) {
    if (sums[i] > 9) {
      exCol = i;
      break;
    }
  }
  const ec = exCol;
  const ecB = [
    parseInt(draw.strips[0][ec], 10),
    parseInt(draw.strips[1][ec], 10),
    parseInt(draw.strips[2][ec], 10),
  ];
  const ecQ = Math.floor(sums[ec] / 10);
  const ecR = sums[ec] - ecQ * 10;

  return {
    isLive,
    round: draw.round,
    cutoffTs: draw.cutoff_ts,
    dateLabel: fmtDateTimeBR(draw.cutoff_ts),
    blocks: draw.blockNumbers.map((n, i) => ({
      number: n,
      timeBR: fmtBRTime(draw.blockTimestamps[i]),
      timeUTC: fmtUtcTime(draw.blockTimestamps[i]),
      ts: draw.blockTimestamps[i],
      hashRaw: draw.hashes[i],
      chars: draw.walks[i].chars,
      last10: draw.strips[i].split(''),
    })),
    sums,
    D,
    m1,
    m2,
    m3,
    dNumber,
    handSum,
    sicboSteps,
    dice,
    sicboSum,
    sicboBigSmall: sicboSum >= 11 && sicboSum <= 17 ? 'big' : 'small',
    rouletteQ: rQ,
    rouletteR: rR,
    rouletteNum: rR,
    rouletteColor,
    lastdigit,
    ldOddEven: lastdigit % 2 === 0 ? 'even' : 'odd',
    ldBigSmall: lastdigit >= 5 ? 'big' : 'small',
    ecCol: ec + 1,
    ecB1: ecB[0],
    ecB2: ecB[1],
    ecB3: ecB[2],
    ecSum: sums[ec],
    ecQ,
    ecR,
    ecD: D[ec],
  };
}

// Dice face SVG (1-6 pips) — port of legacy diceFaceSvg().
export function diceFacePips(n: number): [number, number][] {
  const dotPositions: Record<number, [number, number][]> = {
    1: [[12, 12]],
    2: [[7, 7], [17, 17]],
    3: [[6, 6], [12, 12], [18, 18]],
    4: [[7, 7], [17, 7], [7, 17], [17, 17]],
    5: [[7, 7], [17, 7], [12, 12], [7, 17], [17, 17]],
    6: [[7, 7], [17, 7], [7, 12], [17, 12], [7, 17], [17, 17]],
  };
  return dotPositions[n] || [];
}
